import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Trash2,
  Eye,
  X,
  ToggleLeft,
  ToggleRight,
  Mail,
  Send,
  Users,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
} from "lucide-react";
import { showError, showSuccess } from "../lib/toast";
import {
  server_post_data,
  get_newsletter_subscribers,
  manage_subscriber_status,
  delete_subscriber,
  get_campaign_data,
  send_campaign,
  delete_campaign,
} from "../serviceconnection/ServiceConnection";
import CampaignForm from "./CampaignForm";

// ─── Tab IDs ──────────────────────────────────────────────────────────────────
const TABS = [
  { key: "subscribers", label: "Subscribers" },
  { key: "campaigns", label: "Campaigns" },
];

export default function Newsletter() {
  const [activeTab, setActiveTab] = useState("subscribers");
  const [formPage, setFormPage] = useState({ open: false, editId: null });

  // ── Subscribers state ──────────────────────────────────────────────────────
  const [subscribers, setSubscribers] = useState([]);
  const [subSearch, setSubSearch] = useState("");
  const [subFilter, setSubFilter] = useState(-1); // -1=all,1=active,0=inactive
  const [subLoading, setSubLoading] = useState(true);
  const [deleteSubId, setDeleteSubId] = useState(null);

  // ── Campaigns state ────────────────────────────────────────────────────────
  const [campaigns, setCampaigns] = useState([]);
  const [campSearch, setCampSearch] = useState("");
  const [campFilter, setCampFilter] = useState(-1); // -1=all,0=draft,1=sent,2=failed
  const [campLoading, setCampLoading] = useState(true);
  const [viewCamp, setViewCamp] = useState(null);
  const [deleteCampId, setDeleteCampId] = useState(null);
  const [sendCampId, setSendCampId] = useState(null);
  const [sendLoading, setSendLoading] = useState(false);

  // ── Fetch subscribers ──────────────────────────────────────────────────────
  const fetchSubscribers = async () => {
    try {
      setSubLoading(true);
      const fd = new FormData();
      fd.append("main_id", 0);
      fd.append("per_page", 500);
      const res = await server_post_data(get_newsletter_subscribers, fd);
      setSubscribers(res.data?.message ?? []);
    } catch {
      showError("Failed to load subscribers");
    } finally {
      setSubLoading(false);
    }
  };

  // ── Fetch campaigns ────────────────────────────────────────────────────────
  const fetchCampaigns = async () => {
    try {
      setCampLoading(true);
      const fd = new FormData();
      fd.append("main_id", 0);
      fd.append("per_page", 200);
      const res = await server_post_data(get_campaign_data, fd);
      setCampaigns(res.data?.message ?? []);
    } catch {
      showError("Failed to load campaigns");
    } finally {
      setCampLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
    fetchCampaigns();
  }, []);

  // ── Derived — subscribers ──────────────────────────────────────────────────
  const filteredSubs = subscribers.filter((s) => {
    const matchSearch =
      s.email?.toLowerCase().includes(subSearch.toLowerCase()) ||
      s.source?.toLowerCase().includes(subSearch.toLowerCase());
    const matchFilter =
      subFilter === -1 || (subFilter === 1 ? s.is_active : !s.is_active);
    return matchSearch && matchFilter;
  });

  // ── Derived — campaigns ────────────────────────────────────────────────────
  const filteredCamps = campaigns.filter((c) => {
    const matchSearch = c.subject
      ?.toLowerCase()
      .includes(campSearch.toLowerCase());
    const matchFilter = campFilter === -1 || c.status === campFilter;
    return matchSearch && matchFilter;
  });

  // ── Toggle subscriber ──────────────────────────────────────────────────────
  const handleSubToggle = async (s) => {
    try {
      const fd = new FormData();
      fd.append("main_id", s.primary_id);
      fd.append("is_active", s.is_active ? 0 : 1);
      const res = await server_post_data(manage_subscriber_status, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess(res.data?.message);
      setSubscribers((prev) =>
        prev.map((item) =>
          item.primary_id === s.primary_id
            ? { ...item, is_active: !s.is_active }
            : item,
        ),
      );
    } catch {
      showError("Something went wrong");
    }
  };

  // ── Delete subscriber ──────────────────────────────────────────────────────
  const handleSubDelete = async () => {
    try {
      const fd = new FormData();
      fd.append("main_id", deleteSubId);
      const res = await server_post_data(delete_subscriber, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess("Subscriber deleted");
      setDeleteSubId(null);
      setSubscribers((prev) =>
        prev.filter((s) => s.primary_id !== deleteSubId),
      );
    } catch {
      showError("Something went wrong");
    }
  };

  // ── Delete campaign ────────────────────────────────────────────────────────
  const handleCampDelete = async () => {
    try {
      const fd = new FormData();
      fd.append("main_id", deleteCampId);
      const res = await server_post_data(delete_campaign, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess("Campaign deleted");
      setDeleteCampId(null);
      setCampaigns((prev) => prev.filter((c) => c.primary_id !== deleteCampId));
    } catch {
      showError("Something went wrong");
    }
  };

  // ── Send campaign ──────────────────────────────────────────────────────────
  const handleSendCampaign = async () => {
    try {
      setSendLoading(true);
      const fd = new FormData();
      fd.append("campaign_id", sendCampId);
      const res = await server_post_data(send_campaign, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess(`Sent to ${res.data?.total_sent} subscribers`);
      setSendCampId(null);
      fetchCampaigns();
    } catch {
      showError("Something went wrong");
    } finally {
      setSendLoading(false);
    }
  };

  // ── Campaign form page ─────────────────────────────────────────────────────
  if (formPage.open) {
    return (
      <CampaignForm
        editId={formPage.editId}
        onBack={() => setFormPage({ open: false, editId: null })}
        onSaved={() => {
          setFormPage({ open: false, editId: null });
          fetchCampaigns();
        }}
      />
    );
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  const campStatusBadge = (status) => {
    const map = {
      0: {
        label: "Draft",
        bg: "hsl(var(--muted))",
        color: "hsl(var(--muted-foreground))",
        icon: <Clock className="w-3 h-3" />,
      },
      1: {
        label: "Sent",
        bg: "hsl(var(--success-muted))",
        color: "hsl(var(--success))",
        icon: <CheckCircle className="w-3 h-3" />,
      },
      2: {
        label: "Failed",
        bg: "hsl(var(--destructive-muted))",
        color: "hsl(var(--destructive))",
        icon: <AlertCircle className="w-3 h-3" />,
      },
    };
    const s = map[status] ?? map[0];
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
        style={{ background: s.bg, color: s.color }}
      >
        {s.icon} {s.label}
      </span>
    );
  };

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-AU", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[1400px] mx-auto space-y-5">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Newsletter</h1>
          <p className="page-subtitle">
            Manage subscribers and email campaigns
          </p>
        </div>
        {activeTab === "campaigns" && (
          <button
            onClick={() => setFormPage({ open: true, editId: null })}
            className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white"
            style={{ background: "hsl(var(--accent))" }}
          >
            <Plus className="w-4 h-4" /> New Campaign
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: "Total Subscribers",
            value: subscribers.length,
            color: "hsl(var(--accent))",
            icon: <Users className="w-5 h-5" />,
          },
          {
            label: "Active",
            value: subscribers.filter((s) => s.is_active).length,
            color: "hsl(var(--success))",
            icon: <CheckCircle className="w-5 h-5" />,
          },
          {
            label: "Total Campaigns",
            value: campaigns.length,
            color: "hsl(var(--info))",
            icon: <FileText className="w-5 h-5" />,
          },
          {
            label: "Emails Sent",
            value: campaigns.reduce((a, c) => a + (c.total_sent || 0), 0),
            color: "hsl(var(--warning))",
            icon: <Send className="w-5 h-5" />,
          },
        ].map((s) => (
          <div key={s.label} className="cms-card p-4 flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${s.color}1a`, color: s.color }}
            >
              {s.icon}
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: s.color }}>
                {s.value}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="section-panel p-0">
        <div className="flex border-b border-border px-5">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-5 py-3 text-xs font-semibold border-b-2 transition-colors -mb-px ${
                activeTab === t.key
                  ? "border-accent text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ══ SUBSCRIBERS TAB ══════════════════════════════════════════════════ */}
        {activeTab === "subscribers" && (
          <div className="section-panel-body space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="search-input flex-1 min-w-[200px] max-w-sm">
                <Search className="w-4 h-4" />
                <input
                  value={subSearch}
                  onChange={(e) => setSubSearch(e.target.value)}
                  placeholder="Search by email or source..."
                  className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="flex gap-2">
                {[
                  { v: -1, l: "All" },
                  { v: 1, l: "Active" },
                  { v: 0, l: "Inactive" },
                ].map((f) => (
                  <button
                    key={f.v}
                    onClick={() => setSubFilter(f.v)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      subFilter === f.v
                        ? "text-white border-transparent"
                        : "border-border text-muted-foreground hover:border-accent/30"
                    }`}
                    style={
                      subFilter === f.v
                        ? { background: "hsl(var(--accent))" }
                        : {}
                    }
                  >
                    {f.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full cms-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Source</th>
                    <th>Subscribed On</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subLoading ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="text-center py-10 text-muted-foreground text-sm"
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : filteredSubs.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="text-center py-10 text-muted-foreground text-sm"
                      >
                        No subscribers found
                      </td>
                    </tr>
                  ) : (
                    filteredSubs.map((s) => (
                      <tr key={s.primary_id} className="group">
                        <td>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                              style={{
                                background: "hsl(var(--accent)/0.12)",
                                color: "hsl(var(--accent))",
                              }}
                            >
                              <Mail className="w-3.5 h-3.5" />
                            </div>
                            <span className="text-sm font-medium text-foreground">
                              {s.email}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="text-xs px-2 py-1 rounded-full border border-border text-muted-foreground">
                            {s.source || "—"}
                          </span>
                        </td>
                        <td className="text-sm text-muted-foreground">
                          {fmtDate(s.entry_date)}
                        </td>
                        <td>
                          {s.is_active ? (
                            <span
                              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
                              style={{
                                background: "hsl(var(--success-muted))",
                                color: "hsl(var(--success))",
                              }}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-current" />{" "}
                              Active
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
                              style={{
                                background: "hsl(var(--destructive-muted))",
                                color: "hsl(var(--destructive))",
                              }}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-current" />{" "}
                              Inactive
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleSubToggle(s)}
                              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                              style={{
                                color: s.is_active
                                  ? "hsl(var(--success))"
                                  : "hsl(var(--warning))",
                              }}
                              title="Toggle Status"
                            >
                              {s.is_active ? (
                                <ToggleRight className="w-4 h-4" />
                              ) : (
                                <ToggleLeft className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => setDeleteSubId(s.primary_id)}
                              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                              style={{ color: "hsl(var(--destructive))" }}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-1 py-2 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Showing {filteredSubs.length} of {subscribers.length}{" "}
                subscribers
              </p>
            </div>
          </div>
        )}

        {/* ══ CAMPAIGNS TAB ════════════════════════════════════════════════════ */}
        {activeTab === "campaigns" && (
          <div className="section-panel-body space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="search-input flex-1 min-w-[200px] max-w-sm">
                <Search className="w-4 h-4" />
                <input
                  value={campSearch}
                  onChange={(e) => setCampSearch(e.target.value)}
                  placeholder="Search campaigns..."
                  className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="flex gap-2">
                {[
                  { v: -1, l: "All" },
                  { v: 0, l: "Draft" },
                  { v: 1, l: "Sent" },
                  { v: 2, l: "Failed" },
                ].map((f) => (
                  <button
                    key={f.v}
                    onClick={() => setCampFilter(f.v)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      campFilter === f.v
                        ? "text-white border-transparent"
                        : "border-border text-muted-foreground hover:border-accent/30"
                    }`}
                    style={
                      campFilter === f.v
                        ? { background: "hsl(var(--accent))" }
                        : {}
                    }
                  >
                    {f.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full cms-table">
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Sent On</th>
                    <th>Sent</th>
                    <th>Failed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campLoading ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="text-center py-10 text-muted-foreground text-sm"
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : filteredCamps.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="text-center py-10 text-muted-foreground text-sm"
                      >
                        No campaigns found
                      </td>
                    </tr>
                  ) : (
                    filteredCamps.map((c) => (
                      <tr key={c.primary_id} className="group">
                        <td>
                          <div className="flex items-center gap-3">
                            <div
                              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{
                                background: "hsl(var(--accent)/0.12)",
                                color: "hsl(var(--accent))",
                              }}
                            >
                              <Mail className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="font-semibold text-foreground text-sm line-clamp-1">
                                {c.subject}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Created {fmtDate(c.entry_date)}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td>{campStatusBadge(c.status)}</td>
                        <td className="text-sm text-muted-foreground">
                          {c.sent_at ? fmtDate(c.sent_at) : "—"}
                        </td>
                        <td>
                          <span
                            className="text-sm font-semibold"
                            style={{ color: "hsl(var(--success))" }}
                          >
                            {c.total_sent ?? 0}
                          </span>
                        </td>
                        <td>
                          <span
                            className="text-sm font-semibold"
                            style={{
                              color:
                                c.total_failed > 0
                                  ? "hsl(var(--destructive))"
                                  : "hsl(var(--muted-foreground))",
                            }}
                          >
                            {c.total_failed ?? 0}
                          </span>
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setViewCamp(c)}
                              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
                              title="Preview"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {c.status === 0 && (
                              <>
                                <button
                                  onClick={() =>
                                    setFormPage({
                                      open: true,
                                      editId: c.primary_id,
                                    })
                                  }
                                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
                                  title="Edit"
                                >
                                  <FileText className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setSendCampId(c.primary_id)}
                                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                                  style={{ color: "hsl(var(--accent))" }}
                                  title="Send"
                                >
                                  <Send className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setDeleteCampId(c.primary_id)}
                                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                                  style={{ color: "hsl(var(--destructive))" }}
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-1 py-2 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Showing {filteredCamps.length} of {campaigns.length} campaigns
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── Campaign Preview Modal ───────────────────────────────────────────── */}
      {viewCamp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm p-4"
          onClick={() => setViewCamp(null)}
        >
          <div
            className="section-panel p-0 w-full max-w-2xl max-h-[90vh] overflow-y-auto fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="section-panel-header sticky top-0 z-10">
              <h2 className="section-panel-title">Campaign Preview</h2>
              <button
                onClick={() => setViewCamp(null)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="section-panel-body space-y-4">
              <div className="flex flex-wrap gap-3 items-center justify-between">
                <div>
                  <p className="font-bold text-foreground text-base">
                    {viewCamp.subject}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Created {fmtDate(viewCamp.entry_date)}
                  </p>
                </div>
                {campStatusBadge(viewCamp.status)}
              </div>
              {viewCamp.status === 1 && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg border border-border bg-muted/30 text-center">
                    <p
                      className="text-xl font-bold"
                      style={{ color: "hsl(var(--success))" }}
                    >
                      {viewCamp.total_sent}
                    </p>
                    <p className="text-xs text-muted-foreground">Sent</p>
                  </div>
                  <div className="p-3 rounded-lg border border-border bg-muted/30 text-center">
                    <p
                      className="text-xl font-bold"
                      style={{ color: "hsl(var(--destructive))" }}
                    >
                      {viewCamp.total_failed}
                    </p>
                    <p className="text-xs text-muted-foreground">Failed</p>
                  </div>
                </div>
              )}
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="px-3 py-2 border-b border-border bg-muted/40">
                  <p className="text-xs font-semibold text-muted-foreground">
                    Email Body Preview
                  </p>
                </div>
                <div
                  className="p-4 text-sm"
                  dangerouslySetInnerHTML={{ __html: viewCamp.body_html }}
                />
              </div>
              {viewCamp.status === 0 && (
                <div className="flex justify-end gap-3 pt-1">
                  <button
                    onClick={() => {
                      setViewCamp(null);
                      setFormPage({ open: true, editId: viewCamp.primary_id });
                    }}
                    className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg border border-border hover:bg-muted text-foreground"
                  >
                    <FileText className="w-4 h-4" /> Edit
                  </button>
                  <button
                    onClick={() => {
                      setViewCamp(null);
                      setSendCampId(viewCamp.primary_id);
                    }}
                    className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white"
                    style={{ background: "hsl(var(--accent))" }}
                  >
                    <Send className="w-4 h-4" /> Send Now
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Send Confirm Modal ───────────────────────────────────────────────── */}
      {sendCampId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm"
          onClick={() => !sendLoading && setSendCampId(null)}
        >
          <div
            className="section-panel p-0 w-full max-w-sm mx-4 fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="section-panel-header">
              <h2 className="section-panel-title">Send Campaign</h2>
            </div>
            <div className="section-panel-body space-y-4">
              <div className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/30">
                <Send
                  className="w-5 h-5 mt-0.5 flex-shrink-0"
                  style={{ color: "hsl(var(--accent))" }}
                />
                <p className="text-sm text-muted-foreground">
                  This will send the campaign to{" "}
                  <span className="font-semibold text-foreground">
                    {subscribers.filter((s) => s.is_active).length} active
                    subscribers
                  </span>
                  . This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setSendCampId(null)}
                  disabled={sendLoading}
                  className="px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground hover:bg-muted disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendCampaign}
                  disabled={sendLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: "hsl(var(--accent))" }}
                >
                  <Send className="w-3.5 h-3.5" />
                  {sendLoading ? "Sending..." : "Send Now"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Subscriber Modal ──────────────────────────────────────────── */}
      {deleteSubId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm"
          onClick={() => setDeleteSubId(null)}
        >
          <div
            className="section-panel p-0 w-full max-w-sm mx-4 fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="section-panel-header">
              <h2 className="section-panel-title">Delete Subscriber</h2>
            </div>
            <div className="section-panel-body space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteSubId(null)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubDelete}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
                  style={{ background: "hsl(var(--destructive))" }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Campaign Modal ────────────────────────────────────────────── */}
      {deleteCampId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm"
          onClick={() => setDeleteCampId(null)}
        >
          <div
            className="section-panel p-0 w-full max-w-sm mx-4 fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="section-panel-header">
              <h2 className="section-panel-title">Delete Campaign</h2>
            </div>
            <div className="section-panel-body space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure? Only draft campaigns can be deleted.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteCampId(null)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCampDelete}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
                  style={{ background: "hsl(var(--destructive))" }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
